import React, { useState, useEffect } from 'react';
import { getModels, getModelsSearch, getDownloadLink, addModelToPool, getPoolModels, getModelById, removeModelFromPool } from '../../utils/api';
import DropDown from '../DropDownStructure';
import RenderModels from '../RenderModels';
import IntersectionImg from '../../img/intersectionImg.svg';
import SymmetricDiffImg from '../../img/symmetricDiffImg.svg';
import UnionImg from '../../img/unionImg.svg';
import DifferenceImg from '../../img/differenceImg.svg'

const ModelSelection = ({ onBack, poolId = null, pool = {}, pools = [], isPoolCreation = true }) => {
  // might be too confusing to do the editing, casting and pools like this, using a anumeration type would be better
  const [creationType] = useState(isPoolCreation ? 'Pool' : 'Casting');
  const [oldModels, setOldModels] = useState([]);
  const [poolName, setPoolName] = useState(poolId ? poolId : '');
  const [models, setModels] = useState([]); // Models for current page or search results
  const [selectedModelIds, setSelectedModelIds] = useState([]); // Store only model IDs
  const [previewModels, setPreviewModels] = useState([]); // Full data of preview models
  const [selectedPools, setSelectedPools] = useState([]); // IDs of selected pools
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1); // Current page
  const [totalPages, setTotalPages] = useState(1); // Total pages for current mode
  const [previewPage, setPreviewPage] = useState(1); // Current preview page
  const [totalPreviewPages, setTotalPreviewPages] = useState(1); // Total preview pages
  const [loading, setLoading] = useState(false);
  const [creatingPool, setCreatingPool] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [hoveredOperation, setHoveredOperation] = useState(null);

  const MODELS_PER_PAGE = 10;
  const MODELS_PER_PREVIEW_PAGE = 10;

  // Mapping operations to images
  const operationImages = {
    "Intersection": IntersectionImg,
    "Symmetric Difference": SymmetricDiffImg,
    "Union": UnionImg,
    "Difference": DifferenceImg
  };

  useEffect(() => {
    const fetchPoolModels = async () => {
      try {
        setLoading(true);
        if (pool) {
          const response = await getPoolModels(pool.pool_id);
          setSelectedModelIds(response.data);
          setOldModels(response.data);
        }
      } catch (err) {
        console.error('Error fetching models:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchPoolModels();
  }, [poolId, pool]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const response = searchQuery
          ? await getModelsSearch(page, MODELS_PER_PAGE, searchQuery)
          : await getModels(page, MODELS_PER_PAGE);

        const modelData = response.data.content || [];
        setModels(modelData);
        setTotalPages(response.data.pagination.totalPages);
      } catch (err) {
        console.error('Error fetching models:', err);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
  }, [page, searchQuery]);

  useEffect(() => {
    // Fetch full model data for the current preview page
    const fetchPreviewModels = async () => {
      try {
        setLoading(true);
        //slicing selected models to render a subset of the models
        const startIdx = (previewPage - 1) * MODELS_PER_PREVIEW_PAGE;
        const idsForPreview = selectedModelIds.slice(startIdx, startIdx + MODELS_PER_PREVIEW_PAGE);

        //fetching the full data
        const models = await Promise.all(idsForPreview.map((id) => getModelById(id).then((res) => res.data)));
        setPreviewModels(models);
        //setting the total preview pages
        setTotalPreviewPages(Math.ceil(selectedModelIds.length / MODELS_PER_PREVIEW_PAGE));
      } catch (err) {
        console.error('Error fetching preview models:', err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedModelIds.length > 0) {
      fetchPreviewModels();
    }
  }, [previewPage, selectedModelIds]);

  const handleModelSelect = (model) => {
    if (selectedModelIds.includes(model.id)) {
      // Unselect the model
      setSelectedModelIds((prev) => prev.filter((id) => id !== model.id));
    } else {
      // Select the model
      setSelectedModelIds((prev) => [...prev, model.id]);
    }
  };

  const handlePoolSelect = (pool) => {
    if (selectedPools.some((selectedPool) => selectedPool.pool_id === pool.pool_id)) {
      // Unselect the pool
      setSelectedPools((prev) => prev.filter((pools) => pools.pool_id !== pool.pool_id));
    } else {
      // Select the pool
      setSelectedPools((prev) => [...prev, pool]);
    }
  };

  /**
   * gets all models from all pools and puts them into an array of Sets
   * @returns Array of Sets representing pools
   */
  const getPoolSets = async () => {
    let allModelSets = [];
    // Fetch models from each selected pool
    for (const pool of selectedPools) {
      if (pool.pool_id === "current-selection") {
        allModelSets.push(new Set(selectedModelIds)); // Add current selection as a set
      } else {
        const response = await getPoolModels(pool.pool_id);
        allModelSets.push(new Set(response.data));
      }
    }

    return allModelSets;
  }

  /**
   * gets all models from all pools and puts them into an array
   * @returns Array representing all models from all selected pools
   */
  /**
 * Gets all models from all selected pools and returns a Set of unique models
 * @returns Set representing all models from all selected pools
 */
  const getModelsFromSelectedPools = async () => {
    let allModels = new Set();

    for (const pool of selectedPools) {
      if (pool.pool_id === "current-selection") {
        selectedModelIds.forEach(id => allModels.add(id)); // Add models one by one
      } else {
        const response = await getPoolModels(pool.pool_id);
        response.data.forEach(id => allModels.add(id)); // Add models from API response
      }
    }

    return allModels;
  };


  /**
   * intersect all models from one or multiple pools to the selection, avoiding duplicates
   */
  const handleIntersect = async () => {
    if (selectedPools.length === 0) {
      alert("Please select at least one pool to perform intersection.");
      return;
    }

    try {
      setLoading(true);
      let allModelSets = await getPoolSets();

      // Perform intersection: if only one pool, return all its models
      const intersectedModels = selectedPools.length === 1
        ? Array.from(allModelSets[0])
        : Array.from(allModelSets.reduce((a, b) => new Set([...a].filter(x => b.has(x)))));

      // Update selected models with the intersection result
      setSelectedModelIds((prev) => [...new Set([...prev, ...intersectedModels])]);
    } catch (err) {
      console.error("Error intersecting pools:", err);
    } finally {
      setSelectedPools([]); // Clear selected pools after operation
      setLoading(false);
    }
  };

  /**
   * intersect all models from one or multiple pools to the selection, avoiding duplicates
   */
  const handleSymmetricDifference = async () => {
    if (selectedPools.length === 0) {
      alert("Please select at least one pool to perform symmetric difference.");
      return;
    }

    try {
      setLoading(true);
      let allModelSets = await getPoolSets();

      // Compute symmetric difference
      let symmetricDiffSet = new Set();

      if (selectedPools.length === 1) {
        // If only one pool is selected, return all models from that pool
        symmetricDiffSet = allModelSets[0];
      } else {
        // Compute symmetric difference across multiple pools
        let allModels = new Set(allModelSets.flatMap(set => [...set]));
        console.log("allModels", allModels);
        let commonModels = allModelSets.reduce((a, b) => new Set([...a].filter(x => b.has(x))));
        console.log("commonModels", commonModels);

        // Symmetric difference: models that are in any pool but not in all
        symmetricDiffSet = new Set([...allModels].filter(x => !commonModels.has(x)));
      }

      console.log("symmdiff", symmetricDiffSet);

      // Update selected models with the symmetric difference result
      setSelectedModelIds((prev) => [...new Set([...prev, ...symmetricDiffSet])]);
    } catch (err) {
      console.error("Error computing symmetric difference:", err);
    } finally {
      setSelectedPools([]); // Clear selected pools after operation
      setLoading(false);
    }
  };


  /**
   * add all models from one or multiple pools to the selection, avoiding duplicates
   */
  const handleAddAll = async () => {
    if (selectedPools.length === 0) {
      alert("Please select at least one pool to perform symmetric difference.");
      return;
    }
    try {
      setLoading(true);
      const allModelIds = await getModelsFromSelectedPools();

      //Set avoids duplicates
      setSelectedModelIds((prev) => [...new Set([...prev, ...allModelIds])]);
    } catch (err) {
      console.error('Error fetching models for pool:', err);
    } finally {
      setSelectedPools([]);
      setLoading(false);
    }
  };

  /**
    * Remove all models from one or multiple pools from the selection, avoiding duplicates
    */
  const handleRemoveAll = async () => {
    try {
      setLoading(true);
      const allModelIds = await getModelsFromSelectedPools();

      // Filter out all models that are in the selected pools
      setSelectedModelIds((modelIds) => modelIds.filter((id) => !allModelIds.has(id)));
    } catch (err) {
      console.error('Error fetching models for pool:', err);
    } finally {
      setSelectedPools([]); // Clear selected pools after operation
      setLoading(false);
    }
  };


  const handleCreatePool = async () => {
    //check name and amount of models 
    if (!poolName.trim() || selectedModelIds.length === 0) {
      alert('Please provide a pool name and select at least one model.');
      return;
    }

    try {
      setCreatingPool(true);
      if (!poolId) {
        for (const modelId of selectedModelIds) {
          await addModelToPool(poolName, modelId);
        }
        alert('Pool created successfully!');
      } else {
        //add models that have been selected
        for (const modelId of selectedModelIds) {
          if (!oldModels.includes(modelId)) {
            await addModelToPool(poolName, modelId);
          }
        }
        //remove models that have been unselected
        for (const modelId of oldModels) {
          if (!selectedModelIds.includes(modelId)) {
            await removeModelFromPool(poolName, modelId);
          }
        }
        alert('Pool updated successfully!');
      }
      onBack();
    } catch (error) {
      console.error('Error saving pool:', error);
      alert('Failed to save the pool. Please try again.');
    } finally {
      setCreatingPool(false);
    }
  };

  const fetchThumbnailUrl = (model) => {
    if (model.smallThumb && model.smallThumb.file) {
      return getDownloadLink(model.smallThumb.file);
    }
    return '';
  };

  return (
    <div className="create-pool">
      <h1 className='title'>{poolId != null ? 'Edit ' : 'Create a New '}{creationType}</h1>
      <div className="form-row">
        <input
          id={`${creationType}-name`}
          className="inputtext"
          type="text"
          value={poolName}
          onChange={(e) => setPoolName(e.target.value)}
          placeholder={`Enter ${creationType} name`}
          style={{ width: '30%' }}
        />
      </div>

      <label>Note:</label><br />
      <textarea id="w3review" name="note" rows="4" cols="50" placeholder={`Specify what this ${creationType} is for`}></textarea><br />
      <br />
      <DropDown
        summary={<h2>From Pools</h2>}
        details={
          <div>
            <div className='pool-selection-container'>
              <div className='pool-selection-left-div'>
                {/* Option for the current-selection */}
                <div key="current-selection">
                  <strong>Preview {creationType}</strong>
                  <input
                    type="checkbox"
                    checked={selectedPools.some(p => p.pool_id === "current-selection")}
                    onChange={() =>
                      handlePoolSelect({ pool_id: "current-selection", models: selectedModelIds })
                    }
                    style={{ marginRight: '10px' }}
                  />
                  <br /><br />
                </div>
                {pools.map((pool) => (
                  <div key={pool.pool_id}>
                    {pool.pool_id}
                    <input
                      type="checkbox"
                      checked={selectedPools.includes(pool)}
                      onChange={() => handlePoolSelect(pool)}
                      style={{ marginRight: '10px' }}
                    />
                  </div>
                ))}
              </div>
              <div className='pool-selection-right-div'>
                {hoveredOperation &&
                  <img className="pool-selection-set-img"
                    src={operationImages[hoveredOperation]} />}
              </div>
            </div>
            <div>
              <button
                className='button'
                disabled={selectedPools.length < 2}
                onClick={handleIntersect}
                data-tooltip={selectedPools.length < 2 ? "Select at least two pools" : ""}
                onMouseEnter={() => setHoveredOperation("Intersection")}
                onMouseLeave={() => setHoveredOperation(null)}>
                Intersect
              </button>
              <button
                className='button'
                disabled={selectedPools.length === 0}
                onClick={handleAddAll}
                data-tooltip={selectedPools.length === 0 ? "Select at least one pool" : ""}
                onMouseEnter={() => setHoveredOperation("Union")}
                onMouseLeave={() => setHoveredOperation(null)}>
                Add All
              </button>
              <button
                className='button'
                disabled={selectedPools.length < 2}
                onClick={handleSymmetricDifference}
                data-tooltip={selectedPools.length < 2 ? "Select at least two pools" : ""}
                onMouseEnter={() => setHoveredOperation("Symmetric Difference")}
                onMouseLeave={() => setHoveredOperation(null)}>
                Symmetrical Difference
              </button>
              <button
                className='button'
                disabled={selectedPools.length === 0}
                onClick={handleRemoveAll}
                data-tooltip={selectedPools.length === 0 ? "Select at least one pool" : ""}
                onMouseEnter={() => setHoveredOperation("Difference")}
                onMouseLeave={() => setHoveredOperation(null)}>
                Remove All
              </button>
            </div>
          </div>
        }
      />

      <DropDown
        summary={<h2>From All Models</h2>}
        details={
          <div>
            <input
              id="model-search"
              className="inputtext"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setPage(1);
                setSearchQuery(e.target.value);
              }}
              placeholder="Search models by name"
              style={{ width: '50%', margin: '10px 0' }}
            />
            <div className="models-list">
              {loadingModels ? (
                <p>Loading models...</p>
              ) : models.length > 0 ? (
                <ul>
                  {models.map((model) => (
                    <li key={model.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedModelIds.includes(model.id)}
                          onChange={() => handleModelSelect(model)}
                          style={{ marginRight: '10px' }}
                        />
                        <img
                          src={fetchThumbnailUrl(model)}
                          alt={`${model.firstName || ''} ${model.lastName || ''}`}
                          style={{ width: '40px', height: '40px', marginRight: '10px' }}
                        />
                        {`${model.firstName || ''} ${model.lastName || ''}`.trim() || 'N/A'}
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No models found.</p>
              )}
              <div className="pagination">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((prevPage) => prevPage - 1)}
                >
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((prevPage) => prevPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        }
      />

      <DropDown
        summary={<h2>{creationType} Preview</h2>}
        details={
          loading || loadingModels ? (
            <p>Loading models...</p>
          ) : selectedModelIds.length === 0 ? (
            <div>{creationType} Empty</div>
          ) : (
            <div>
              <RenderModels models={previewModels} />
              <div className="pagination">
                <button
                  disabled={previewPage === 1}
                  onClick={() => setPreviewPage((prevPage) => prevPage - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {previewPage} of {totalPreviewPages}
                </span>
                <button
                  disabled={previewPage === totalPreviewPages}
                  onClick={() => setPreviewPage((prevPage) => prevPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )
        }
        expand={true}
      />

      <div className="actions">
        <button className="button" onClick={isPoolCreation ? handleCreatePool : () => { }} disabled={creatingPool}>
          {creatingPool ? `Saving ${creationType}...` : `Save ${creationType}`}
        </button>
        <button className="button" onClick={onBack}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ModelSelection;
